/**
 * "Columns" manager launcher for a data_list (ADR-0010 G1).
 *
 * Per-column configuration would bloat the inspector sidebar (and it will GROW
 * — filter today, then column chooser / pinning / label / width). So the
 * sidebar shows just a compact **Configure columns** button that opens a modal
 * hosting the full per-column editor. New per-column settings get added as
 * columns of that table, not as new sidebar rows.
 */
import { Columns3 } from "lucide-react";
import { useState } from "react";

import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { ColumnFilterConfigEditor } from "./ColumnFilterConfigEditor";

export function ColumnsManagerField({
  value,
  columns,
  loadValues,
  showFilter = true,
  showVisibility = false,
  showPin = false,
  onChange,
}: {
  value: unknown;
  columns: string[];
  loadValues?: (column: string) => Promise<string[]>;
  /** G1: show the per-column filter-mode picker (filterable on). */
  showFilter?: boolean;
  /** G2: also show the per-column "visible by default" toggle (chooser on). */
  showVisibility?: boolean;
  /** G3: also show the per-column "pin left" toggle (pinnable on). */
  showPin?: boolean;
  onChange: (config: Record<string, unknown>) => void;
}) {
  const [open, setOpen] = useState(false);
  const count = Object.keys(value && typeof value === "object" ? (value as object) : {}).length;

  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/50 py-2">
      <span className="text-xs text-muted-foreground">Columns</span>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium shadow-sm transition hover:bg-accent/5"
      >
        <Columns3 className="h-3.5 w-3.5" />
        Configure{count > 0 ? ` (${count})` : ""}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Columns</DialogTitle>
          </DialogHeader>
          <DialogBody className="max-h-[60vh] overflow-y-auto">
            <ColumnFilterConfigEditor
              value={value}
              columns={columns}
              loadValues={loadValues}
              showFilter={showFilter}
              showVisibility={showVisibility}
              showPin={showPin}
              onChange={onChange}
            />
          </DialogBody>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-foreground hover:bg-accent/90"
            >
              Done
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
